import { Check } from '@phosphor-icons/react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import clsx from 'clsx'
import type { FC, Ref } from 'react'
import React, { useEffect, useRef, useState } from 'react'

import type { IAlertDialogType } from '@/components/shared/ui/AlertDialog/AlertDialog'
import Label from '@/components/shared/ui/Label'

import AlertDialog from '../AlertDialog'
import CheckboxStyles from './Checkbox.module.css'

const CheckboxIndicator: FC<any> = React.forwardRef(
  ({ ...props }, forwardedRef: Ref<any>) => (
    <CheckboxPrimitive.Indicator
      {...props}
      ref={forwardedRef}
      className={CheckboxStyles['hnui-checkbox-indicator']}
    >
      <Check weight='bold' className={CheckboxStyles['hnui-checkbox-icon']} />
    </CheckboxPrimitive.Indicator>
  )
)

export interface ICheckboxProps {
  label?: string
  id?: string
  description?: string
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  isSwitch?: boolean
  onCheckedChange?: (checked: boolean) => void
  alertVariant?: IAlertDialogType
  alertTexts?: {
    title: string
    description?: string
    cancelText?: string
    confirmText?: string
  }
  disabledAlertTexts?: {
    title: string
    description?: string
    cancelText?: string
    confirmText?: string
  }
  alertDisabled?: boolean
  checkboxClassName?: string
  onClick?: (e: React.MouseEvent) => void
  leftToRight?: boolean
}

const SwitchThumb: FC<any> = React.forwardRef(
  ({ ...props }, forwardedRef: Ref<any>) => (
    <SwitchPrimitive.Thumb
      {...props}
      ref={forwardedRef}
      className={CheckboxStyles['hnui-switch-thumb']}
    />
  )
)

const Checkbox: FC<ICheckboxProps> = React.forwardRef(
  (
    {
      label,
      description,
      checked: _checked,
      defaultChecked,
      disabled,
      onCheckedChange,
      isSwitch,
      alertVariant,
      alertTexts,
      alertDisabled,
      disabledAlertTexts = {},
      checkboxClassName,
      id,
      leftToRight,
      ...props
    },
    ref: Ref<any>
  ) => {
    const [checked, setChecked] = useState<boolean | undefined>(_checked)
    const [showAlert, setShowAlert] = useState(false)
    const valueToSet = useRef<Boolean | null>()

    const handleConfirm = async () => {
      if (
        onCheckedChange &&
        (valueToSet.current !== null || valueToSet.current !== undefined)
      ) {
        await onCheckedChange(valueToSet.current as boolean)
        setChecked(!!valueToSet.current)
        return true
      }
      setShowAlert(false)
      return true
    }

    const handleCancel = () => {
      setShowAlert(false)
      setChecked(!valueToSet.current)
      valueToSet.current = null
    }

    const handleToggle = (newChecked: boolean) => {
      if (alertTexts) {
        setShowAlert(true)
        valueToSet.current = newChecked
      } else if (onCheckedChange) {
        onCheckedChange(newChecked)
      }
    }

    useEffect(() => {
      setChecked(_checked)
    }, [_checked])

    const renderCheckbox = () => {
      if (isSwitch) {
        return (
          <SwitchPrimitive.Root
            ref={ref}
            id={id || label}
            checked={checked}
            disabled={disabled}
            defaultChecked={defaultChecked}
            onCheckedChange={handleToggle}
            className={clsx(
              CheckboxStyles['hnui-switch'],
              disabled ? '!cursor-not-allowed opacity-50' : ''
            )}
            {...props}
          >
            <SwitchThumb />
          </SwitchPrimitive.Root>
        )
      }
      return (
        <CheckboxPrimitive.Root
          ref={ref}
          id={id || label}
          checked={checked}
          disabled={disabled}
          defaultChecked={defaultChecked}
          onCheckedChange={handleToggle}
          className={clsx(
            CheckboxStyles['hnui-label-checkbox'],
            checkboxClassName,
            {
              '!cursor-not-allowed': disabled,
            }
          )}
          {...props}
        >
          <CheckboxIndicator />
        </CheckboxPrimitive.Root>
      )
    }

    const alertTextsMerged = checked
      ? { ...alertTexts, ...disabledAlertTexts }
      : alertTexts

    return (
      <>
        <Label htmlfor={id || label}>
          <span
            className={clsx(CheckboxStyles['hnui-label-contener'], {
              '!items-center': !description,
              'flex-row-reverse': leftToRight,
            })}
          >
            {renderCheckbox()}
            {label && (
              <span className={clsx(CheckboxStyles['hnui-checkbox-label'])}>
                <span
                  className={CheckboxStyles['hnui-checkbox-label-name']}
                  dangerouslySetInnerHTML={{ __html: label }}
                />
                {description ? (
                  <span
                    className={CheckboxStyles['hnui-checkbox-description']}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                ) : null}
              </span>
            )}
          </span>
        </Label>
        <AlertDialog
          type={alertVariant || 'info'}
          disabled={alertDisabled}
          open={showAlert}
          title={alertTextsMerged?.title}
          description={alertTextsMerged?.description}
          confirmText={alertTextsMerged?.confirmText}
          cancelText={alertTextsMerged?.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          waitForPromise={true}
          onClose={() => setShowAlert(false)}
        />
      </>
    )
  }
)

SwitchThumb.displayName = 'SwitchThumb'
Checkbox.displayName = 'Checkbox'
CheckboxIndicator.displayName = 'CheckboxIndicator'
export default Checkbox
